import React, { useState, useRef, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  Input,
  FilterBar,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
} from '../../../design-system/components';
import { formatCurrency, formatDate } from '../../../lib/formatters';
import {
  admin_commissions_path,
  admin_affiliate_path,
  bulk_update_status_admin_commissions_path,
} from '../../../lib/routes';
import { cn } from '../../../design-system/utils/cn';

const statusVariant = {
  pending: 'warning',
  approved: 'success',
  paid: 'success',
  reversed: 'danger',
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'reversed', label: 'Reversed' },
];

const columns = [
  {
    id: 'affiliate_name',
    header: 'Affiliate',
    render: (row) => (
      <Link href={admin_affiliate_path(row.affiliate_id)} className="block hover:underline">
        <div className="font-medium text-fg">{row.affiliate_name}</div>
        <div className="text-xs text-fg-tertiary">{row.affiliate_slug}</div>
      </Link>
    ),
  },
  {
    id: 'customer_email',
    header: 'Customer',
    render: (row) => (
      <span className="text-sm text-fg-secondary">{row.customer_email || '—'}</span>
    ),
  },
  {
    id: 'sale',
    header: 'Sale',
    render: (row) => (
      <span className="text-sm text-fg-secondary">
        {formatCurrency(row.invoice_amount_cents)} / {row.billing_interval === 'yearly' ? 'yr' : 'mo'}
      </span>
    ),
  },
  {
    id: 'commission_amount_cents',
    header: 'Commission',
    sortable: true,
    render: (row) => (
      <div>
        <span className="font-medium">{formatCurrency(row.commission_amount_cents)}</span>
        <span className="ml-1 text-xs text-fg-tertiary">
          ({(row.commission_rate_snapshot * 100).toFixed(0)}%)
        </span>
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={statusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
  {
    id: 'created_at',
    header: 'Date',
    sortable: true,
    render: (row) => (
      <span className="text-sm text-fg-tertiary">{formatDate(row.created_at)}</span>
    ),
  },
];

export default function Index({
  commissions,
  affiliates,
  campaigns,
  filters,
  pagination,
  is_super_admin,
}) {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [targetStatus, setTargetStatus] = useState('pending');
  const [initialStatus, setInitialStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const debounceRef = useRef(null);

  const handleSearch = useCallback((value) => {
    clearTimeout(debounceRef.current);
    setSearchValue(value);
    debounceRef.current = setTimeout(() => {
      router.visit(admin_commissions_path(), {
        data: { ...filters, search: value || undefined, page: '1' },
        preserveState: true,
        preserveScroll: true,
        replace: true,
      });
    }, 300);
  }, [filters]);

  const selectedCount = selectedRows.size;
  const selectedTotal = commissions
    .filter((c) => selectedRows.has(c.id))
    .reduce((sum, c) => sum + c.commission_amount_cents, 0);

  function handleSort(columnId) {
    const newDirection = filters.sort === columnId && filters.direction === 'desc' ? 'asc' : 'desc';
    const sortParam =
      columnId === 'commission_amount_cents' ? 'commission_amount_cents' : 'created_at';
    router.visit(admin_commissions_path(), {
      data: {
        ...filters,
        sort: sortParam,
        direction: newDirection,
      },
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  }

  function handlePageChange(page) {
    router.visit(admin_commissions_path(), {
      data: {
        ...filters,
        page: String(page),
      },
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  }

  function handleBulkUpdateStatus() {
    setLoading(true);
    router.post(
      bulk_update_status_admin_commissions_path(),
      {
        commission_ids: Array.from(selectedRows),
        status: targetStatus,
      },
      {
        onFinish: () => {
          setLoading(false);
          setShowStatusModal(false);
          setSelectedRows(new Set());
        },
      },
    );
  }

  const filterDefs = [
    {
      param: 'status',
      label: 'Status',
      value: filters.status || '',
      options: [
        { label: 'Pending', value: 'pending' },
        { label: 'Paid', value: 'paid' },
        { label: 'Reversed', value: 'reversed' },
      ],
    },
    {
      param: 'affiliate_id',
      label: 'Affiliate',
      value: filters.affiliate_id || '',
      options: affiliates.map((a) => ({
        label: a.name,
        value: String(a.id),
      })),
    },
    {
      param: 'campaign_id',
      label: 'Campaign',
      value: filters.campaign_id || '',
      options: campaigns.map((c) => ({
        label: c.name,
        value: String(c.id),
      })),
    },
  ];

  const activeSortColumn = filters.sort === 'commission_amount_cents' ? 'commission_amount_cents' : 'created_at';

  const bulkActionBar = (
    <div className="flex items-center gap-3">
      <Button size="sm" onClick={() => {
        const selected = commissions.filter((c) => selectedRows.has(c.id));
        const statuses = new Set(selected.map((c) => c.status));
        const current = statuses.size === 1 ? [...statuses][0] : selected[0]?.status ?? 'pending';
        setTargetStatus(current);
        setInitialStatus(current);
        setShowStatusModal(true);
      }}>
        Set Status ({selectedCount})
      </Button>
      <span className="text-sm text-fg-secondary">
        Total: {formatCurrency(selectedTotal)}
      </span>
    </div>
  );

  return (
    <AdminLayout>
      <PageHeader title="Commissions" description="Full ledger of affiliate commissions" />

      <div className="mt-6 space-y-4">
        <Input
          placeholder="Search by affiliate name or email…"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <FilterBar url={admin_commissions_path()} filters={filterDefs} />

        <div className="flex gap-3">
          <Input
            label="From"
            type="date"
            value={filters.date_from || ''}
            onChange={(e) => {
              router.visit(admin_commissions_path(), {
                data: {
                  ...filters,
                  date_from: e.target.value,
                  page: '1',
                },
                preserveState: true,
                preserveScroll: true,
                replace: true,
              });
            }}
          />
          <Input
            label="To"
            type="date"
            value={filters.date_to || ''}
            onChange={(e) => {
              router.visit(admin_commissions_path(), {
                data: {
                  ...filters,
                  date_to: e.target.value,
                  page: '1',
                },
                preserveState: true,
                preserveScroll: true,
                replace: true,
              });
            }}
          />
        </div>

        <DataTable
          columns={columns}
          data={commissions}
          keyAccessor="id"
          selectable={is_super_admin}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          bulkActions={is_super_admin ? bulkActionBar : null}
          sortColumn={activeSortColumn}
          sortDirection={filters.direction || 'desc'}
          onSort={(columnId) => handleSort(columnId)}
          pagination={{
            page: pagination.current_page,
            totalPages: pagination.total_pages,
            onPageChange: handlePageChange,
          }}
          emptyTitle="No commissions found"
          emptyDescription="Commissions will appear here as affiliates generate referrals."
        />
      </div>

      <Modal
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        size="sm"
      >
        <ModalTitle>Update Commission Status</ModalTitle>
        <ModalBody>
          <p className="text-sm text-fg-secondary">
            Change status for {selectedCount} commission{selectedCount !== 1 ? 's' : ''} ({formatCurrency(selectedTotal)}).
          </p>
          <div className="mt-4">
            <label htmlFor="target-status" className="block text-sm font-medium text-fg-secondary mb-1.5">
              New status
            </label>
            <select
              id="target-status"
              value={targetStatus}
              onChange={(e) => setTargetStatus(e.target.value)}
              className={cn(
                'w-full rounded-md border border-edge bg-surface px-3 py-2 text-sm',
                'text-fg focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25',
              )}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={() => setShowStatusModal(false)} disabled={loading}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleBulkUpdateStatus} loading={loading} disabled={targetStatus === initialStatus}>
            Update Status
          </Button>
        </ModalFooter>
      </Modal>
    </AdminLayout>
  );
}
